function routineHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` };
}

export interface RecentRoutineRun {
  id: number;
  routineId: string;
  routineName: string;
  status: 'done' | 'failed' | 'running' | string;
  startedAt: string;
  finishedAt?: string;
  durationSec?: number;
}

// Last N runs across ALL of the current user's routines — backs the
// /momentum-routines right-sidebar "Recent Runs" feed.
export async function getRecentRoutineRuns(limit = 10): Promise<RecentRoutineRun[]> {
  const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/MomentumRoutine/runs/recent?limit=${limit}`, {
    headers: routineHeaders(),
  });
  if (!res.ok) throw new Error('Failed to load recent routine runs');
  const json = await res.json();
  return Array.isArray(json?.data) ? json.data : [];
}

export interface AgentRoutine {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  lastRunAt: string | null;
  updatedAt: string | null;
}

// The current user's routines assigned to one Digital Agent, most recently run
// first — backs the "Routines" tab of the agent's recent-activity panel. The list
// endpoint has no agent filter, so this reads up to 100 routines and keeps the
// agent's (by id, or by name for routines saved before ids were stored).
export async function getAgentRoutines(agentId: string, agentName: string): Promise<AgentRoutine[]> {
  const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/MomentumRoutine/list?page=1&pageSize=100`, {
    method: 'POST',
    headers: routineHeaders(),
    body: JSON.stringify({ searchBy: null }),
  });
  if (!res.ok) throw new Error('Failed to load routines');
  const json = await res.json();
  const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
  const name = agentName.trim().toLowerCase();
  const at = (r: AgentRoutine) => new Date(r.lastRunAt || r.updatedAt || 0).getTime() || 0;
  return list
    .filter(r => (r.assigneeAgentId
      ? r.assigneeAgentId === agentId
      : !!name && String(r.assigneeAgentName ?? '').trim().toLowerCase() === name))
    .map(r => ({
      id: String(r.id),
      name: String(r.name ?? ''),
      description: String(r.description ?? ''),
      isActive: !!r.isActive,
      lastRunAt: r.lastRunAt ?? null,
      updatedAt: r.updatedAt ?? r.createdAt ?? null,
    }))
    .sort((a, b) => at(b) - at(a));
}

export interface RoutineStats {
  total: number;
  active: number;
  paused: number;
}

// All-time totals across ALL of the current user's routines, independent of
// whatever page/search filter the list UI has applied — backs the
// /momentum-routines right-sidebar KPI cards.
export async function getRoutineStats(): Promise<RoutineStats> {
  const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/MomentumRoutine/stats`, {
    headers: routineHeaders(),
  });
  if (!res.ok) throw new Error('Failed to load routine stats');
  const json = await res.json();
  return {
    total: json?.total ?? 0,
    active: json?.active ?? 0,
    paused: json?.paused ?? 0,
  };
}
